/**
 * Shared frame for the printable documents (tracker item 26): an on-screen
 * toolbar (Back / Print, hidden on paper), the company letterhead, the
 * document body, and the footer note. No app nav: the /print/* routes sit
 * outside AppLayout.
 *
 * Nothing renders on paper until every fetch has succeeded; a failed fetch
 * shows an error instead of a half-empty document, and auto-print only fires
 * once the document (and its logo, if any) is ready.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import { primaryButtonClass, secondaryButtonClass } from './formStyles';
import { AUTO_PRINT_PARAM, companyAddressLines, companyContactLine } from '../pages/print/printUi';
import { Toast } from './Toast';

export interface PrintDataState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  retry: () => void;
}

/**
 * Loads a print document whenever `requestKey` changes. `load` should fetch
 * everything the document needs (including company settings) in one promise,
 * so any failure is a single error state and nothing partial is shown.
 */
export function usePrintData<T>(requestKey: string, load: () => Promise<T>, fallbackError: string): PrintDataState<T> {
  const loadRef = useRef(load);
  loadRef.current = load;
  const [reload, setReload] = useState(0);
  const [state, setState] = useState<{ data: T | null; loading: boolean; error: string | null }>({ data: null, loading: true, error: null });

  useEffect(() => {
    let active = true;
    setState({ data: null, loading: true, error: null });
    loadRef.current()
      .then((data) => { if (active) setState({ data, loading: false, error: null }); })
      .catch((cause) => { if (active) setState({ data: null, loading: false, error: getErrorMessage(cause, fallbackError) }); });
    return () => { active = false; };
  }, [requestKey, reload, fallbackError]);

  const retry = useCallback(() => setReload((value) => value + 1), []);
  return { ...state, retry };
}

/** Longest wait for the logo before auto-print goes ahead without it. */
const LOGO_WAIT_MS = 3000;

interface PrintLayoutProps<T> {
  /** Browser tab title; Chrome also uses it as the default "Save as PDF" file name. */
  title: string;
  backTo: string;
  backLabel: string;
  /** Noun for the loading message ("invoice", "service order", "statement"). */
  noun: string;
  state: PrintDataState<T>;
  /** Company settings from the loaded data (null until loaded). */
  company: types.CompanySettings | null;
  children: React.ReactNode;
}

export function PrintLayout<T>({ title, backTo, backLabel, noun, state, company, children }: PrintLayoutProps<T>) {
  const [params, setParams] = useSearchParams();
  const autoPrint = params.get(AUTO_PRINT_PARAM) === '1';
  const ready = Boolean(state.data && company && !state.loading && !state.error);
  const logoUrl = company?.logoUrl?.trim() || '';
  const [logoSettled, setLogoSettled] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const printed = useRef(false);

  useEffect(() => { setLogoSettled(false); setLogoFailed(false); }, [logoUrl]);

  useEffect(() => {
    if (!ready) return undefined;
    const previous = document.title;
    document.title = title;
    return () => { document.title = previous; };
  }, [ready, title]);

  // Don't hold printing hostage to a slow or unreachable logo.
  useEffect(() => {
    if (!ready || !logoUrl || logoSettled) return undefined;
    const timer = window.setTimeout(() => setLogoSettled(true), LOGO_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [ready, logoUrl, logoSettled]);

  // Open the print dialog once, after the document has rendered. The ref
  // survives StrictMode's double effects; dropping the flag from the URL
  // stops a reload of this tab from printing again.
  useEffect(() => {
    if (!autoPrint || !ready || printed.current || (logoUrl && !logoSettled)) return undefined;
    const timer = window.setTimeout(() => {
      if (printed.current) return;
      printed.current = true;
      window.print();
      setParams((current) => { const next = new URLSearchParams(current); next.delete(AUTO_PRINT_PARAM); return next; }, { replace: true });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [autoPrint, ready, logoUrl, logoSettled, setParams]);

  return (
    <div className="print-root min-h-screen bg-gray-100 pb-10">
      <div className="print:hidden sticky top-0 z-10 border-b border-gray-200 bg-white/95 backdrop-blur" role="toolbar" aria-label="Print toolbar">
        <div className="mx-auto flex max-w-[8.5in] flex-wrap items-center justify-between gap-3 px-4 py-3">
          <Link to={backTo} className={`${secondaryButtonClass} inline-flex items-center`}>&larr; {backLabel}</Link>
          <button type="button" className={primaryButtonClass} disabled={!ready} onClick={() => window.print()}>Print</button>
        </div>
      </div>

      {state.loading && <p role="status" className="mx-auto mt-10 max-w-[8.5in] px-4 text-gray-600">Loading {noun}…</p>}

      {!state.loading && state.error && (
        <div className="mx-auto mt-10 max-w-[8.5in] px-4">
          <Toast kind="error">{state.error}</Toast>
          <p className="text-gray-700">This {noun} could not be loaded.</p>
          <button type="button" className={`${secondaryButtonClass} mt-4`} onClick={state.retry}>Retry</button>
        </div>
      )}

      {ready && company && (
        <article className="print-sheet mx-auto mt-4 max-w-[8.5in] bg-white px-4 py-6 text-sm text-gray-900 shadow sm:mt-6 sm:px-10 sm:py-10">
          <CompanyLetterhead
            company={company}
            showLogo={!logoFailed}
            onLogoLoad={() => setLogoSettled(true)}
            onLogoError={() => { setLogoFailed(true); setLogoSettled(true); }}
          />
          {children}
          <CompanyFooterNote company={company} />
        </article>
      )}
    </div>
  );
}

interface CompanyLetterheadProps {
  company: types.CompanySettings;
  /** False once the logo failed to load (the header then prints without it). */
  showLogo?: boolean;
  onLogoLoad?: () => void;
  onLogoError?: () => void;
}

/**
 * The company header printed at the top of every document (and of every work
 * order copy, tracker item 58): logo, name, address, contact and tax id.
 */
export function CompanyLetterhead({ company, showLogo = true, onLogoLoad, onLogoError }: CompanyLetterheadProps) {
  const logoUrl = company.logoUrl?.trim() || '';
  const addressLines = companyAddressLines(company);
  const contact = companyContactLine(company);
  return (
    <header className="print-keep mb-8 flex items-start justify-between gap-6 border-b border-gray-300 pb-6">
      <div className="flex min-w-0 flex-wrap items-start gap-4 sm:flex-nowrap print:flex-nowrap">
        {logoUrl && showLogo && (
          <img
            src={logoUrl}
            alt={`${company.companyName} logo`}
            className="max-h-20 max-w-[10rem] object-contain"
            onLoad={onLogoLoad}
            onError={onLogoError}
          />
        )}
        <div className="min-w-0 break-words">
          <p className="text-lg font-bold">{company.companyName}</p>
          {addressLines.map((line) => <p key={line}>{line}</p>)}
          {contact && <p>{contact}</p>}
          {company.taxId && <p>Tax ID: {company.taxId}</p>}
        </div>
      </div>
    </header>
  );
}

/** The company footer note printed at the end of every document (nothing when blank). */
export function CompanyFooterNote({ company }: { company: types.CompanySettings }) {
  const footerNote = company.invoiceFooterNote?.trim();
  if (!footerNote) return null;
  return <footer className="print-keep mt-10 border-t border-gray-300 pt-4 text-xs whitespace-pre-wrap text-gray-700">{footerNote}</footer>;
}
